import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, ArrowDownRight, Coins } from 'lucide-react';
import { queryKeys } from '@/api/queryKeys';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { comma, dateTime } from '@/lib/format';

interface PointLog {
  id: number;
  type: 'earn' | 'spend';
  action: string;
  amount: number;
  balance: number;
  meta: Record<string, unknown> | null;
  created_at: string;
}

const ACTION_LABEL: Record<string, string> = {
  signup_bonus: '가입 보너스',
  checkin: '출석체크',
  checkin_streak_7: '7일 연속 출석 보너스',
  checkin_streak_30: '30일 연속 출석 보너스',
  write_post: '게시글 작성',
  write_post_premium: '게시글 작성 (프리미엄)',
  write_comment: '댓글 작성',
  write_comment_premium: '댓글 작성 (프리미엄)',
  write_diary: '매매일지 작성',
  exchange: '교환소 이용',
  premium_purchase: '프리미엄 구독',
  refund: '환불',
  exchange_refund: '교환 환불',
  exchange_expired: '교환 미수령 환불 (구 정책)',
  referral_referrer: '친구 추천 보상',
  referral_referee: '친구 추천 가입',
  alt_checkin: '대체 출석',
  diary_public: '공개 매매일지',
  diary_buy_reason: '매매일지 매수사유',
  diary_target_price: '매매일지 목표가',
  diary_stop_loss: '매매일지 손절가',
  diary_sell_complete: '매매일지 매도완료',
  point_expired: '포인트 만료 소멸',
};

function labelForAction(action: string): string {
  return ACTION_LABEL[action] ?? action;
}

export function PointHistoryPage() {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.point.history(),
    queryFn: () => get<{ logs: PointLog[] }>('/points/history'),
  });

  const logs = data?.logs ?? [];
  const currentBalance = logs[0]?.balance ?? 0;

  return (
    <div className="container-base py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-extrabold">포인트 내역</h1>
        <p className="mt-1 text-sm text-[var(--color-text-sub)]">
          최근 100건까지 표시돼요.
        </p>
      </header>

      {/* 현재 잔액 카드 */}
      <section className="mb-6 rounded-2xl border border-[var(--color-border)] bg-gradient-to-br from-[var(--color-primary)]/10 to-transparent p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-primary)]/15">
            <Coins className="h-6 w-6 text-[var(--color-primary)]" />
          </div>
          <div>
            <div className="text-xs font-semibold text-[var(--color-text-sub)]">
              현재 잔액
            </div>
            <div className="text-3xl font-extrabold tabular-nums text-[var(--color-primary)]">
              {comma(currentBalance)}
              <span className="ml-1 text-base">P</span>
            </div>
          </div>
        </div>
      </section>

      {/* 내역 리스트 */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : logs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-sm text-[var(--color-text-sub)]">
          아직 포인트 내역이 없어요.
        </div>
      ) : (
        <ul className="divide-y divide-[var(--color-border)] overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          {logs.map((log) => {
            const isEarn = log.type === 'earn';
            return (
              <li
                key={log.id}
                className="flex items-center gap-3 px-4 py-3"
              >
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-full ${
                    isEarn
                      ? 'bg-[var(--color-rise)]/10'
                      : 'bg-[var(--color-fall)]/10'
                  }`}
                >
                  {isEarn ? (
                    <ArrowUpRight className="h-4 w-4 text-[var(--color-rise)]" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-[var(--color-fall)]" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">
                    {labelForAction(log.action)}
                  </div>
                  <div className="text-xs text-[var(--color-text-sub)]">
                    {dateTime(log.created_at)}
                  </div>
                </div>

                <div className="text-right">
                  <div
                    className={`text-sm font-bold tabular-nums ${
                      isEarn
                        ? 'text-[var(--color-rise)]'
                        : 'text-[var(--color-fall)]'
                    }`}
                  >
                    {isEarn ? '+' : '-'}
                    {comma(Math.abs(log.amount))}P
                  </div>
                  <div className="text-xs tabular-nums text-[var(--color-text-sub)]">
                    잔액 {comma(log.balance)}P
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
